import type { JsonObject } from "@/domains/common/types";
import type { Uuid } from "@/domains/global/identifiers";
import type { SyncModelScope } from "@/domains/sync-scopes/types";
import type { SyncUpdatePartialValue, SyncUpdateShared, SyncUpdateValue } from "@/store/sync/types";

type AssistantUpsertedSyncUpdateValue<ModelKind extends string, ModelData> = {
  model_id: Uuid;
  model_kind: ModelKind;
  model_version: number;
  model_scopes: SyncModelScope[];
  model_data: ModelData;
};

export const AssistantTaskStatus = {
  Unknown: "unknown",
  Pending: "pending",
  InProgress: "in_progress",
  Completed: "completed",
  Canceled: "canceled",
} as const;

export type AssistantTaskStatus = (typeof AssistantTaskStatus)[keyof typeof AssistantTaskStatus];

export const AssistantProjectStatus = {
  Unknown: "unknown",
  Active: "active",
  Resolved: "resolved",
  Archived: "archived",
} as const;

export type AssistantProjectStatus =
  (typeof AssistantProjectStatus)[keyof typeof AssistantProjectStatus];

export const AssistantFollowUpStatus = {
  Unknown: "unknown",
  Pending: "pending",
  Fired: "fired",
  Completed: "completed",
  Canceled: "canceled",
} as const;

export type AssistantFollowUpStatus =
  (typeof AssistantFollowUpStatus)[keyof typeof AssistantFollowUpStatus];

export const AssistantRoutineStatus = {
  Unknown: "unknown",
  Active: "active",
  Paused: "paused",
  Unreviewed: "unreviewed",
} as const;

export type AssistantRoutineStatus =
  (typeof AssistantRoutineStatus)[keyof typeof AssistantRoutineStatus];

export const AssistantRoutineKind = {
  Unknown: "unknown",
  Wakeup: "wakeup",
  SelfHealing: "self_healing",
  Custom: "custom",
} as const;

export type AssistantRoutineKind = (typeof AssistantRoutineKind)[keyof typeof AssistantRoutineKind];

export const AssistantSkillStatus = {
  Active: "active",
  Paused: "paused",
} as const;

export type AssistantSkillStatus = (typeof AssistantSkillStatus)[keyof typeof AssistantSkillStatus];

export const AssistantSkillOrigin = {
  UserCreated: "user_created",
  AgentCreated: "agent_created",
} as const;

export type AssistantSkillOrigin = (typeof AssistantSkillOrigin)[keyof typeof AssistantSkillOrigin];

export type AssistantTaskModelData = {
  space_account_id: Uuid;
  assistant_id: Uuid;
  project_id: Uuid | null;
  title: string;
  description: string | null;
  status: AssistantTaskStatus;
  due_at: string | null;
  created_at: string;
  updated_at: string;
};

export type AssistantProjectModelData = {
  space_account_id: Uuid;
  assistant_id: Uuid;
  title: string;
  description: string | null;
  status: AssistantProjectStatus;
  last_activity_at: string | null;
  created_at: string;
  updated_at: string;
};

export type AssistantFollowUpModelData = {
  space_account_id: Uuid;
  assistant_id: Uuid;
  project_id: Uuid | null;
  task_id: Uuid | null;
  source_context_id: Uuid | null;
  content: string;
  status: AssistantFollowUpStatus;
  scheduled_for: string;
  fired_at: string | null;
  completed_at: string | null;
  commitment_kind: string | null;
  due_at: string | null;
  deadline_at: string | null;
  trigger_type: string | null;
  trigger_not_before: string | null;
  trigger_not_after: string | null;
  created_at: string;
  updated_at: string;
};

export type AssistantTaskLabelGroupModelData = {
  space_account_id: Uuid;
  assistant_id: Uuid;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
};

export type AssistantTaskLabelModelData = {
  space_account_id: Uuid;
  assistant_id: Uuid;
  group_id: Uuid | null;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
};

export type AssistantTaskLabelAssignmentModelData = {
  space_account_id: Uuid;
  assistant_id: Uuid;
  task_label_id: Uuid;
  task_id: Uuid;
  created_at: string;
  updated_at: string;
};

export type AssistantTaskNoteReferenceModelData = {
  space_account_id: Uuid;
  assistant_id: Uuid;
  task_id: Uuid;
  note_id: Uuid;
  created_at: string;
  updated_at: string;
};

export type AssistantProjectLabelGroupModelData = {
  space_account_id: Uuid;
  assistant_id: Uuid;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
};

export type AssistantProjectLabelModelData = {
  space_account_id: Uuid;
  assistant_id: Uuid;
  group_id: Uuid | null;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
};

export type AssistantProjectLabelAssignmentModelData = {
  space_account_id: Uuid;
  assistant_id: Uuid;
  project_label_id: Uuid;
  project_id: Uuid;
  created_at: string;
  updated_at: string;
};

export type AssistantTaskActivityModelData = {
  space_account_id: Uuid;
  assistant_id: Uuid;
  task_id: Uuid;
  kind: string;
  payload_json: JsonObject;
  created_at: string;
  updated_at: string;
};

export type AssistantProjectActivityModelData = {
  space_account_id: Uuid;
  assistant_id: Uuid;
  project_id: Uuid;
  kind: string;
  payload_json: JsonObject;
  created_at: string;
  updated_at: string;
};

export type AssistantNoteActivityModelData = {
  space_account_id: Uuid;
  assistant_id: Uuid;
  note_id: Uuid;
  kind: string;
  payload_json: JsonObject;
  created_at: string;
  updated_at: string;
};

export type AssistantRoutineModelData = {
  space_account_id: Uuid;
  assistant_id: Uuid;
  label: string;
  description: string;
  kind: AssistantRoutineKind;
  status: AssistantRoutineStatus;
  fixed: boolean;
  config_json: JsonObject;
  next_run_at: string | null;
  last_run_at: string | null;
  created_at: string;
  updated_at: string;
};

export type AssistantSkillModelData = {
  space_account_id: Uuid;
  assistant_id: Uuid;
  name: string;
  when_to_use: string;
  instructions: string;
  status: AssistantSkillStatus;
  origin: AssistantSkillOrigin;
  public_id: Uuid | null;
  created_at: string;
  updated_at: string;
};

export type AssistantObjectiveModelData = {
  space_account_id: Uuid;
  assistant_id: Uuid;
  scope_kind: string;
  scope_key: string;
  key: string;
  status: string;
  priority: string;
  title: string;
  prompt: string;
  blocking: boolean;
  context_json: JsonObject;
  completed_at: string | null;
  dismissed_at: string | null;
  created_at: string;
  updated_at: string;
};

export type AssistantNoteReviewStatusModelData = {
  space_account_id: Uuid;
  assistant_id: Uuid;
  note_id: Uuid;
  reviewed_note_version: number;
  reviewed_at: string;
  created_at: string;
  updated_at: string;
};

export type AssistantSpaceAccountSettingsModelData = {
  space_account_id: Uuid;
  assistant_id: Uuid;
  timezone: string | null;
  preferred_name: string | null;
  communication_slack_enabled: boolean;
  communication_push_notification_enabled: boolean;
  communication_email_enabled: boolean;
  email_sender_allowlist: AssistantEmailAllowlistEntry[];
  created_at: string;
  updated_at: string;
};

export type AssistantEmailAllowlistEntry = {
  kind: "email" | "domain";
  value: string;
};

export type AssistantConversationModelData = {
  space_account_id: Uuid;
  assistant_id: Uuid;
  last_activity_at: string;
  created_via: string;
  client_metadata_json: JsonObject;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
};

export type AssistantConversationItemModelData = {
  space_account_id: Uuid;
  assistant_id: Uuid;
  conversation_id: Uuid;
  sequence: number;
  kind: string;
  payload_json: JsonObject;
  mem_agent_run_id: Uuid | null;
  created_at: string;
  updated_at: string;
};

export type AssistantObjectiveStatus = AssistantObjectiveModelData["status"];

export type AssistantTaskUpsertedSyncUpdateValue = AssistantUpsertedSyncUpdateValue<
  "ASSISTANT_TASK",
  AssistantTaskModelData
>;

export type AssistantProjectUpsertedSyncUpdateValue = AssistantUpsertedSyncUpdateValue<
  "ASSISTANT_PROJECT",
  AssistantProjectModelData
>;

export type AssistantFollowUpUpsertedSyncUpdateValue = AssistantUpsertedSyncUpdateValue<
  "ASSISTANT_FOLLOW_UP",
  AssistantFollowUpModelData
>;

export type AssistantTaskLabelGroupUpsertedSyncUpdateValue = AssistantUpsertedSyncUpdateValue<
  "ASSISTANT_TASK_LABEL_GROUP",
  AssistantTaskLabelGroupModelData
>;
export type AssistantTaskLabelUpsertedSyncUpdateValue = AssistantUpsertedSyncUpdateValue<
  "ASSISTANT_TASK_LABEL",
  AssistantTaskLabelModelData
>;
export type AssistantTaskLabelAssignmentUpsertedSyncUpdateValue = AssistantUpsertedSyncUpdateValue<
  "ASSISTANT_TASK_LABEL_ASSIGNMENT",
  AssistantTaskLabelAssignmentModelData
>;
export type AssistantTaskNoteReferenceUpsertedSyncUpdateValue = AssistantUpsertedSyncUpdateValue<
  "ASSISTANT_TASK_NOTE_REFERENCE",
  AssistantTaskNoteReferenceModelData
>;
export type AssistantProjectLabelGroupUpsertedSyncUpdateValue = AssistantUpsertedSyncUpdateValue<
  "ASSISTANT_PROJECT_LABEL_GROUP",
  AssistantProjectLabelGroupModelData
>;
export type AssistantProjectLabelUpsertedSyncUpdateValue = AssistantUpsertedSyncUpdateValue<
  "ASSISTANT_PROJECT_LABEL",
  AssistantProjectLabelModelData
>;
export type AssistantProjectLabelAssignmentUpsertedSyncUpdateValue =
  AssistantUpsertedSyncUpdateValue<
    "ASSISTANT_PROJECT_LABEL_ASSIGNMENT",
    AssistantProjectLabelAssignmentModelData
  >;
export type AssistantTaskActivityUpsertedSyncUpdateValue = AssistantUpsertedSyncUpdateValue<
  "ASSISTANT_TASK_ACTIVITY",
  AssistantTaskActivityModelData
>;
export type AssistantProjectActivityUpsertedSyncUpdateValue = AssistantUpsertedSyncUpdateValue<
  "ASSISTANT_PROJECT_ACTIVITY",
  AssistantProjectActivityModelData
>;
export type AssistantNoteActivityUpsertedSyncUpdateValue = AssistantUpsertedSyncUpdateValue<
  "ASSISTANT_NOTE_ACTIVITY",
  AssistantNoteActivityModelData
>;
export type AssistantRoutineUpsertedSyncUpdateValue = AssistantUpsertedSyncUpdateValue<
  "ASSISTANT_ROUTINE",
  AssistantRoutineModelData
>;
export type AssistantSkillUpsertedSyncUpdateValue = AssistantUpsertedSyncUpdateValue<
  "ASSISTANT_SKILL",
  AssistantSkillModelData
>;
export type AssistantObjectiveUpsertedSyncUpdateValue = AssistantUpsertedSyncUpdateValue<
  "ASSISTANT_OBJECTIVE",
  AssistantObjectiveModelData
>;
export type AssistantNoteReviewStatusUpsertedSyncUpdateValue = AssistantUpsertedSyncUpdateValue<
  "ASSISTANT_NOTE_REVIEW_STATUS",
  AssistantNoteReviewStatusModelData
>;
export type AssistantSpaceAccountSettingsUpsertedSyncUpdateValue = AssistantUpsertedSyncUpdateValue<
  "ASSISTANT_SPACE_ACCOUNT_SETTINGS",
  AssistantSpaceAccountSettingsModelData
>;
export type AssistantConversationUpsertedSyncUpdateValue = AssistantUpsertedSyncUpdateValue<
  "ASSISTANT_CONVERSATION",
  AssistantConversationModelData
>;
export type AssistantConversationItemUpsertedSyncUpdateValue = AssistantUpsertedSyncUpdateValue<
  "ASSISTANT_CONVERSATION_ITEM",
  AssistantConversationItemModelData
>;
export type AssistantTaskDeletedSyncUpdateValue = SyncUpdatePartialValue;
export type AssistantProjectDeletedSyncUpdateValue = SyncUpdatePartialValue;
export type AssistantFollowUpDeletedSyncUpdateValue = SyncUpdatePartialValue;
export type AssistantTaskLabelGroupDeletedSyncUpdateValue = SyncUpdatePartialValue;
export type AssistantTaskLabelDeletedSyncUpdateValue = SyncUpdatePartialValue;
export type AssistantTaskLabelAssignmentDeletedSyncUpdateValue = SyncUpdatePartialValue;
export type AssistantProjectLabelGroupDeletedSyncUpdateValue = SyncUpdatePartialValue;
export type AssistantProjectLabelDeletedSyncUpdateValue = SyncUpdatePartialValue;
export type AssistantProjectLabelAssignmentDeletedSyncUpdateValue = SyncUpdatePartialValue;
export type AssistantTaskActivityDeletedSyncUpdateValue = SyncUpdatePartialValue;
export type AssistantProjectActivityDeletedSyncUpdateValue = SyncUpdatePartialValue;
export type AssistantNoteActivityDeletedSyncUpdateValue = SyncUpdatePartialValue;
export type AssistantRoutineDeletedSyncUpdateValue = SyncUpdatePartialValue;
export type AssistantSkillDeletedSyncUpdateValue = {
  model_id: Uuid;
  model_kind: "ASSISTANT_SKILL";
  model_version: number;
};
export type AssistantObjectiveDeletedSyncUpdateValue = SyncUpdatePartialValue;
export type AssistantNoteReviewStatusDeletedSyncUpdateValue = SyncUpdatePartialValue;
export type AssistantSpaceAccountSettingsDeletedSyncUpdateValue = SyncUpdatePartialValue;
export type AssistantConversationDeletedSyncUpdateValue = SyncUpdatePartialValue;
export type AssistantConversationItemDeletedSyncUpdateValue = SyncUpdatePartialValue;
export type AssistantTaskSyncUpdateValue =
  | AssistantTaskUpsertedSyncUpdateValue
  | AssistantTaskDeletedSyncUpdateValue;
export type AssistantProjectSyncUpdateValue =
  | AssistantProjectUpsertedSyncUpdateValue
  | AssistantProjectDeletedSyncUpdateValue;
export type AssistantFollowUpSyncUpdateValue =
  | AssistantFollowUpUpsertedSyncUpdateValue
  | AssistantFollowUpDeletedSyncUpdateValue;
export type AssistantTaskLabelGroupSyncUpdateValue =
  | AssistantTaskLabelGroupUpsertedSyncUpdateValue
  | AssistantTaskLabelGroupDeletedSyncUpdateValue;
export type AssistantTaskLabelSyncUpdateValue =
  | AssistantTaskLabelUpsertedSyncUpdateValue
  | AssistantTaskLabelDeletedSyncUpdateValue;
export type AssistantTaskLabelAssignmentSyncUpdateValue =
  | AssistantTaskLabelAssignmentUpsertedSyncUpdateValue
  | AssistantTaskLabelAssignmentDeletedSyncUpdateValue;
export type AssistantProjectLabelGroupSyncUpdateValue =
  | AssistantProjectLabelGroupUpsertedSyncUpdateValue
  | AssistantProjectLabelGroupDeletedSyncUpdateValue;
export type AssistantProjectLabelSyncUpdateValue =
  | AssistantProjectLabelUpsertedSyncUpdateValue
  | AssistantProjectLabelDeletedSyncUpdateValue;
export type AssistantProjectLabelAssignmentSyncUpdateValue =
  | AssistantProjectLabelAssignmentUpsertedSyncUpdateValue
  | AssistantProjectLabelAssignmentDeletedSyncUpdateValue;
export type AssistantTaskActivitySyncUpdateValue =
  | AssistantTaskActivityUpsertedSyncUpdateValue
  | AssistantTaskActivityDeletedSyncUpdateValue;
export type AssistantProjectActivitySyncUpdateValue =
  | AssistantProjectActivityUpsertedSyncUpdateValue
  | AssistantProjectActivityDeletedSyncUpdateValue;
export type AssistantNoteActivitySyncUpdateValue =
  | AssistantNoteActivityUpsertedSyncUpdateValue
  | AssistantNoteActivityDeletedSyncUpdateValue;
export type AssistantRoutineSyncUpdateValue =
  | AssistantRoutineUpsertedSyncUpdateValue
  | AssistantRoutineDeletedSyncUpdateValue;
export type AssistantSkillSyncUpdateValue =
  | AssistantSkillUpsertedSyncUpdateValue
  | AssistantSkillDeletedSyncUpdateValue;
export type AssistantObjectiveSyncUpdateValue =
  | AssistantObjectiveUpsertedSyncUpdateValue
  | AssistantObjectiveDeletedSyncUpdateValue;
export type AssistantNoteReviewStatusSyncUpdateValue =
  | AssistantNoteReviewStatusUpsertedSyncUpdateValue
  | AssistantNoteReviewStatusDeletedSyncUpdateValue;
export type AssistantSpaceAccountSettingsSyncUpdateValue =
  | AssistantSpaceAccountSettingsUpsertedSyncUpdateValue
  | AssistantSpaceAccountSettingsDeletedSyncUpdateValue;
export type AssistantConversationSyncUpdateValue =
  | AssistantConversationUpsertedSyncUpdateValue
  | AssistantConversationDeletedSyncUpdateValue;
export type AssistantConversationItemSyncUpdateValue =
  | AssistantConversationItemUpsertedSyncUpdateValue
  | AssistantConversationItemDeletedSyncUpdateValue;
export type AssistantTaskIndexData = {
  assistant_id: string;
  project_id: string;
  status: string;
  due_at: string;
  created_at: string;
  updated_at: string;
};

export type AssistantProjectIndexData = {
  assistant_id: string;
  status: string;
  last_activity_at: string;
  created_at: string;
  updated_at: string;
};

export type AssistantFollowUpIndexData = {
  assistant_id: string;
  project_id: string;
  task_id: string;
  source_context_id: string;
  status: string;
  scheduled_for: string;
  created_at: string;
  updated_at: string;
};

export type AssistantRoutineIndexData = {
  assistant_id: string;
  kind: string;
  status: string;
  next_run_at: string;
  last_run_at: string;
  created_at: string;
  updated_at: string;
};

export type AssistantSkillIndexData = {
  assistant_id: string;
  status: string;
  name: string;
  created_at: string;
  updated_at: string;
};

export type IndexedAssistantTaskSyncUpdateValue = SyncUpdateValue<AssistantTaskModelData> &
  AssistantTaskIndexData;
export type IndexedAssistantProjectSyncUpdateValue = SyncUpdateValue<AssistantProjectModelData> &
  AssistantProjectIndexData;
export type IndexedAssistantFollowUpSyncUpdateValue = SyncUpdateValue<AssistantFollowUpModelData> &
  AssistantFollowUpIndexData;
export type IndexedAssistantRoutineSyncUpdateValue = SyncUpdateValue<AssistantRoutineModelData> &
  AssistantRoutineIndexData;
export type IndexedAssistantSkillSyncUpdateValue = SyncUpdateValue<AssistantSkillModelData> &
  AssistantSkillIndexData;

type AssistantSyncUpdateShared = Omit<SyncUpdateShared, "kind" | "value">;

type AssistantUpsertedSyncUpdate<UpsertedValue> = AssistantSyncUpdateShared & {
  kind: "UPSERTED";
  value: UpsertedValue;
};

type AssistantDeletedSyncUpdate<DeletedValue> = AssistantSyncUpdateShared & {
  kind: "DELETED";
  value: DeletedValue;
};

export type AssistantTaskUpsertedSyncUpdate =
  AssistantUpsertedSyncUpdate<AssistantTaskUpsertedSyncUpdateValue>;
export type AssistantTaskDeletedSyncUpdate =
  AssistantDeletedSyncUpdate<AssistantTaskDeletedSyncUpdateValue>;
export type AssistantTaskSyncUpdate =
  | AssistantTaskUpsertedSyncUpdate
  | AssistantTaskDeletedSyncUpdate;

export type AssistantProjectUpsertedSyncUpdate =
  AssistantUpsertedSyncUpdate<AssistantProjectUpsertedSyncUpdateValue>;
export type AssistantProjectDeletedSyncUpdate =
  AssistantDeletedSyncUpdate<AssistantProjectDeletedSyncUpdateValue>;
export type AssistantProjectSyncUpdate =
  | AssistantProjectUpsertedSyncUpdate
  | AssistantProjectDeletedSyncUpdate;

export type AssistantFollowUpUpsertedSyncUpdate =
  AssistantUpsertedSyncUpdate<AssistantFollowUpUpsertedSyncUpdateValue>;
export type AssistantFollowUpDeletedSyncUpdate =
  AssistantDeletedSyncUpdate<AssistantFollowUpDeletedSyncUpdateValue>;
export type AssistantFollowUpSyncUpdate =
  | AssistantFollowUpUpsertedSyncUpdate
  | AssistantFollowUpDeletedSyncUpdate;
